import React from 'react';
import { Box } from '@mui/material';
import { LocationOn } from '@mui/icons-material';
import { Chip } from 'cprime-foundational-components';
import TooltipWrapper from 'components/TooltipWrapper/TooltipWrapper';
import TypographyMarked from 'components/TypographyMarked/TypographyMarked';
import { formatDataValue } from 'utils/formatFns';
import { nsoSpecialOrderFormat } from 'pages/VehicleDetails/sectionMappingVehicleInquiry/nationalSoldOrders';
import { generateTestId } from 'utils/general';
import { chipColorMap } from 'utils/colorMappers';
import { getChipSx } from '../utils/utils';
import { getNOMValue } from './companyCarFields';

export const orderInfoFields = {
  customerName: {
    label: 'Customer Name',
    getValue: (data) => ({
      TMLI: data?.companyCar?.teamMemberLeaseIndicator,
      companyCarIndicator: data?.companyCar?.companyCarIndicator,
      customerName: data?.orderInfo?.orderCustomerName,
    }),
    formatValue: ({ TMLI, companyCarIndicator, customerName }) => {
      const shouldShowCustomerName = companyCarIndicator || TMLI;
      const value = shouldShowCustomerName ? customerName : 'N/A';

      return getNOMValue({
        TMLI,
        value: formatDataValue({ value }),
      });
    },
  },
  dpmsSpecialOrder: {
    label: 'DPMS Special Order',
    getValue: (data) => ({
      specialOrderType: data?.orderInfo?.specialOrderType,
      specialOrderIndicator: data?.orderInfo?.specialOrderIndicator,
    }),
    formatValue: ({ specialOrderType, specialOrderIndicator }) => {
      if (specialOrderType === 'DPMS') {
        return specialOrderIndicator ? 'Y' : 'N';
      }
      return 'N/A';
    },
  },
  exportOrderNumber: {
    label: 'Export Order Number',
    getValue: (data) => data?.orderInfo?.exportOrderNumber,
    formatValue: (value) => formatDataValue({ value }),
  },
  getsudo: {
    label: 'Getsudo',
    getValue: (data) => data?.orderInfo?.getSudoFmt,
    formatValue: (value) => formatDataValue({ value }),
  },
  globalDistributorCodeChip: {
    label: 'Global Distributor Code',
    getValue: (data) => ({
      exportIndicator: data?.generalInfo?.vehicleAttrs?.exportIndicator,
      globalDistrictCode: data?.orderInfo?.globalDistributorCode,
    }),
    formatValue: ({ exportIndicator, globalDistrictCode }, options = {}) => {
      if (!exportIndicator || !globalDistrictCode) {
        return null;
      }

      return (
        <TooltipWrapper tooltipText="Global Distributor Code">
          <Box>
            <Chip
              data-testid={generateTestId(
                'VehicleRecord',
                'VehicleDetailsHeader',
                'global-district-code-chip',
              )}
              label={
                options.isMobile ? (
                  ''
                ) : (
                  <TypographyMarked
                    variant="label"
                    component="span"
                    value={globalDistrictCode}
                    fieldId="vehicle-hero-global-distributor-code"
                    searchContextValues={options.searchContextValues}
                    isPrint={options.isPrint}
                  />
                )
              }
              icon={
                options.isMobile ? (
                  <LocationOn color={chipColorMap.lifecycleChipBackground} />
                ) : null
              }
              color={chipColorMap.lifecycleChipText}
              backgroundColor={chipColorMap.lifecycleChipBackground}
              sx={getChipSx(options)}
            />
          </Box>
        </TooltipWrapper>
      );
    },
  },
  nsoSpecialOrder: {
    label: 'NSO Special Order',
    getValue: (data) => ({
      specialOrderType: data?.orderInfo?.specialOrderType,
      specialOrderIndicator: data?.orderInfo?.specialOrderIndicator,
    }),
    formatValue: ({ specialOrderType, specialOrderIndicator }) =>
      nsoSpecialOrderFormat(specialOrderType, specialOrderIndicator),
  },
  orderArea: {
    label: 'Order Area',
    getValue: (data) => data?.orderInfo?.orderArea,
    formatValue: (value) => formatDataValue({ value }),
  },
  orderAreaPort: {
    label: 'Order Area | Port',
    getValue: (data) => ({
      orderArea: data?.orderInfo?.orderArea,
      portEntryShortName: data?.logistics?.portEntryShortName,
    }),
    formatValue: ({ orderArea, portEntryShortName }) => {
      const formattedArea = formatDataValue({ value: orderArea });
      const formattedPort = formatDataValue({ value: portEntryShortName });
      return `${formattedArea} | ${formattedPort}`;
    },
  },
  priorityBuildDate: {
    label: 'Priority Build Date',
    getValue: (data) => ({
      fromDate: data?.orderInfo?.priorityBuildFromDateFmt,
      toDate: data?.orderInfo?.priorityBuildToDateFmt,
    }),
    formatValue: ({ fromDate, toDate }) => {
      const formattedFrom = formatDataValue({ value: fromDate });
      const formattedTo = formatDataValue({ value: toDate });
      return `${formattedFrom} - ${formattedTo}`;
    },
  },
};
